import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import AddCouponForm from "./AddCouponForm";
import Pagination from "../../sharedComponents/Pagination";
import DeleteConfirmationModal from "./DeleteConfirmationModal";
import SuccessUpdateModal from "./SuccessUpdateModal";
import { PlusIcon } from "@heroicons/react/24/outline";
import {
  DelateCoupon,
  fetchAllCoupons,
  toggleCouponStatus,
} from "../../../redux/reducer/couponsReducer";
import {
  ArrowPathIcon,
  EyeIcon,
  EyeSlashIcon,
  TrashIcon,
} from "@heroicons/react/24/solid";
import { toast } from "react-toastify";
import LoadingSpinner from "../../animation/LoadingSpinner";
import {
  fetchCouponSetting,
  toggleCouponSettingStatus,
  updateCouponSetting,
} from "../../../redux/reducer/couponSettingReducer";

const ShowCoupon = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { allCoupons, loading, error, totalPages } = useSelector(
    (state) => state.coupon
  );
  const { couponSetting } = useSelector((state) => state.couponSetting);

  const [showForm, setShowForm] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(5);
  const [searchBox, setSearchBox] = useState("");
  const [openInfoId, setOpenInfoId] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [selectedCouponId, setSelectedCouponId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // main Form
  const [discountAmount, setDiscountAmount] = useState(
    couponSetting?.baseAmount
  );

  const itemsPerPage = 5;
  // console.log(allCoupons);
  useEffect(() => {
    setDiscountAmount(couponSetting?.baseAmount);
    const fetchData = async () => {
      try {
        setIsLoading(true);
        await dispatch(
          fetchAllCoupons({ page, limit, search: searchBox })
        ).unwrap();
        await dispatch(fetchCouponSetting()).unwrap();
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [dispatch, page, limit, searchBox]);

  // ✅ Filter and paginate
  // const filteredCoupons = allCoupons.filter((coupon) =>
  //   coupon.code.toLowerCase().includes(searchBox.toLowerCase())
  // );

  // const totalPages = Math.ceil(filteredCoupons.length / itemsPerPage);
  const startIndex = (page - 1) * itemsPerPage;

  const handleTrashClick = (couponId) => {
    setSelectedCouponId(couponId);
    setDeleteModalOpen(true);
  };
  const toggleInfo = (_id) => {
    setOpenInfoId((prevId) => (prevId === _id ? null : _id));
    // console.log(openInfoId);
  };
  const handleDeleteConfirm = async () => {
    try {
      setIsLoading(true);
      await dispatch(DelateCoupon(selectedCouponId)).unwrap();
      await dispatch(fetchAllCoupons({ page, limit, search: searchBox }));
      setIsLoading(false);
    } catch (error) {
      console.error("Toggle status error:", error);
      toast.error("Failed to update coupon status");
      setIsLoading(false);
    }
    setDeleteModalOpen(false);
    setSelectedCouponId(null);
  };
  const handleToggleStatus = async (couponId) => {
    try {
      setIsLoading(true);
      await dispatch(toggleCouponStatus(couponId)).unwrap();
      await dispatch(fetchAllCoupons({ page, limit, search: searchBox }));
      setIsLoading(false);
    } catch (error) {
      console.error("Toggle status error:", error);
      toast.error("Failed to update coupon status");
    }
  };
  const handleUpdateCoupon = async () => {
    try {
      setIsLoading(true);
      const payload = { data: { baseAmount: discountAmount } };
      await dispatch(updateCouponSetting(payload)).unwrap();
      setUpdateModalOpen(true);

      // setUpdateModalOpen(false);
    } catch (error) {
      console.error("Failed to update coupon:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggle = async () => {
    try {
      setIsLoading(true);
      await dispatch(toggleCouponSettingStatus()).unwrap();
    } catch (error) {
      console.error("Failed to toggle coupon:", error);
    } finally {
      setIsLoading(false);
    }
  };
  // console.log(discountAmount === couponSetting?.baseAmount);
  // console.log(discountAmount);
  // console.log(couponSetting?.baseAmount);
  // if (isLoading) {
  //   return <LoadingSpinner />;
  // }
  return (
    <div className="w-full overflow-x-auto">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold text-black">{t("Coupons")}</h1>
        <div className="flex items-center gap-3">
          <input
            type="text"
            placeholder="Search by code"
            value={searchBox}
            onChange={(e) => {
              setSearchBox(e.target.value);
              setPage(1);
            }}
            className="px-2 py-2 text-black bg-white border"
          />
          <button
            className="flex items-center gap-2 px-3 py-2 bg-custom-yellow hover:bg-black group"
            onClick={() => setShowForm(true)}
          >
            <PlusIcon className="w-6 h-6 text-black group-hover:text-white" />
            <span className="text-black group-hover:text-white">
              Add Coupon
            </span>
          </button>
        </div>
      </div>

      {loading ? (
        <div>
          <LoadingSpinner />
        </div>
      ) : error ? (
        <div className="text-center text-red-500">{error}</div>
      ) : allCoupons.length > 0 ? (
        <table className="min-w-full mb-8 text-sm border border-gray-200 divide-y divide-gray-200">
          <thead className="font-bold cursor-pointer bg-custom-yellow">
            <tr>
              <th className="px-4 py-2 text-center">{t("ID")}</th>
              <th className="px-4 py-2 text-center">{t("Code Number")}</th>
              <th className="px-4 py-2 text-center">{t("Discount value")}</th>
              <th className="px-4 py-2 text-center">{t("Starte At")}</th>
              <th className="px-4 py-2 text-center">{t("Expiry date")}</th>
              <th className="px-4 py-2 text-center">{t("Total number")}</th>
              <th className="px-4 py-2 text-center">{t("Num. users")}</th>
              <th className="px-4 py-2 text-center">{t("Info")}</th>
              <th className="px-4 py-2 text-center">{t("Actions")}</th>
            </tr>
          </thead>

          <tbody className="bg-white divide-y divide-gray-200">
            {allCoupons.map((discount, index) => (
              <tr key={discount._id}>
                <td className="px-4 py-2 text-center">
                  {startIndex + index + 1}
                </td>
                <td className="px-4 py-2 text-center">{discount.code}</td>
                <td className="px-4 py-2 text-center">
                  {discount.baseAmount} KWD
                </td>
                <td className="px-4 py-2 text-center">
                  {new Date(discount.startAt).toLocaleDateString()}
                </td>
                <td className="px-4 py-2 text-center">
                  {new Date(discount.expiresAt).toLocaleDateString()}
                </td>
                <td className="px-4 py-2 text-center">{discount.maxUsage}</td>
                <td className="px-4 py-2 text-center">{discount.usageCount}</td>

                <td className="px-4 py-2 text-center">
                  <div className="relative inline-block info-dropdown">
                    <button
                      onClick={() => toggleInfo(discount._id)}
                      className="flex px-2 py-2 text-xs text-black cursor-pointer bg-custom-yellow"
                    >
                      Show Info
                    </button>
                    {openInfoId === discount._id && (
                      <div className="absolute left-1/2 -translate-x-1/2 z-50 w-[250px] px-2 py-1 mt-2 text-sm  bg-white border  shadow text-center">
                        {discount.description}
                      </div>
                    )}
                  </div>
                </td>

                <td className="flex justify-center gap-4 px-4 py-2 text-center">
                  {discount.isActive ? (
                    <EyeIcon
                      className="w-5 h-5 text-blue-500 cursor-pointer"
                      onClick={() => handleToggleStatus(discount._id)}
                    />
                  ) : (
                    <EyeSlashIcon
                      className="w-5 h-5 text-gray-400 cursor-pointer"
                      onClick={() => handleToggleStatus(discount._id)}
                    />
                  )}
                  <TrashIcon
                    className="w-5 h-5 text-red-500 cursor-pointer"
                    onClick={() => handleTrashClick(discount._id)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="text-gray-500">No coupons found</div>
      )}

      {/* ✅ Pagination */}
      <div className="flex flex-col items-center mb-4 text-center sm:flex-row sm:items-start sm:text-left">
        <div className="w-full sm:w-1/2"></div>
        <div className="flex justify-center w-full sm:w-1/2 sm:justify-end">
          <Pagination
            currentPage={page}
            totalPages={totalPages ? totalPages : 1}
            onPageChange={(newPage) => setPage(newPage)}
          />
        </div>
      </div>

      {/* Footer section with input and switch */}
      <section className="flex mb-8">
        <div className="flex justify-between w-full">
          <div className="flex items-center">
            <span className="flex items-center justify-center gap-3 px-3 py-3 border border-custom-yellow bg-custom-yellow">
              <span className="text-black me-4">Login Coupons</span>
            </span>
            <div className="inline-flex items-center gap-3 px-3 py-2 bg-white border border-gray-200">
              <input
                type="number"
                placeholder="0"
                min="0"
                value={discountAmount ?? couponSetting?.baseAmount ?? 0}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  if (value >= 0) {
                    setDiscountAmount(value);
                  }
                }}
                className="w-20 text-black border-none focus:outline-none focus:ring-0
                [-moz-appearance:textfield]
                [&::-webkit-outer-spin-button]:appearance-none
                [&::-webkit-inner-spin-button]:appearance-none"
              />
              <span className="font-medium text-black">KWD</span>
              <button
                className={`flex items-center gap-3 px-3 py-1 border border-black hover:bg-black group
                    ${
                      discountAmount === couponSetting?.baseAmount
                        ? "opacity-50 cursor-not-allowed bg-gray-200 text-gray-500 border-gray-300"
                        : "hover:bg-black hover:text-white text-black bg-transparent"
                    }
                  `}
                disabled={discountAmount === couponSetting?.baseAmount}
                onClick={handleUpdateCoupon}
              >
                {isLoading ? (
                  <ArrowPathIcon className="w-10 h-10 p-2 text-white bg-yellow-500 rounded-full animate-spin" />
                ) : (
                  <span className="mx-4 text-black group-hover:text-white">
                    Edit
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Toggle for System Login */}
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-800">
              {couponSetting?.discountIsActive
                ? "🔛 System Coupon is ON"
                : "⛔ System Coupon is OFF"}
            </span>
            <button
              // onClick={() => setIsEnabled(!isEnabled)}
              onClick={() => handleToggle()}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-300 ${
                couponSetting?.discountIsActive
                  ? "bg-custom-yellow"
                  : "bg-gray-300"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-300 ${
                  couponSetting?.discountIsActive
                    ? "translate-x-6"
                    : "translate-x-1"
                }`}
              />
            </button>
          </div>
        </div>
      </section>

      {showForm && <AddCouponForm onClose={() => setShowForm(false)} />}

      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
      />
      <SuccessUpdateModal
        isOpen={updateModalOpen}
        onClose={() => setUpdateModalOpen(false)}
      />
    </div>
  );
};

export default ShowCoupon;
